import { useDispatch, useSelector } from "react-redux";
import SectionHeader from "../../../Common/SectionHeader";
import Input from "../../../../../components/common/Inputs/Input/Input";
import { selectDynamics, setDynamicsCycleRunning } from "../../../../../store/slices/cartesian";
import t, { tr } from "../../../../../utils/common/translation/translator";
import "./CycleRunning.scss";

const CycleRunning = () => {
  const dispatch = useDispatch();
  const dynamics = useSelector(selectDynamics);
  const moves = dynamics.steps;
  const cycleRunning = dynamics?.cycleRunning;

  const handleInputChange = (property) => (e) => {
    dispatch(
      setDynamicsCycleRunning({
        ...cycleRunning,
        [property]: e.target.value === "" ? e.target.value : Number(e.target.value)
      })
    );
  };

  /**
   * Function preCalcTotalCycleTime provides the sum of traveltime and dwelltime for a set of moves.
   *
   * @param {*} moves
   * @returns int
   */
  const preCalcTotalCycleTime = (moves) => {
    var preCalcTotalCycleTime = 0;

    for (const m of moves) {
      preCalcTotalCycleTime += m.travelTime + m.dwellTime;
    }

    return preCalcTotalCycleTime;
  };

  /**
   * Function totalCycleHourMax provides maximum cycles amount per hour found for a set of moves.
   *
   * @param {*} moves
   * @returns int
   */
  const maxNumberCyclesPerHour = (moves) => {
    return Math.round((60 * 60 * 1000) / preCalcTotalCycleTime(moves));
  };

  /**
   * Function adaptValue adapt value for "numberOfCyclePerHour" input only if needed,
   * returns other value instead.
   *
   * @param {*} input
   * @returns int
   */
  const adaptValue = (input) => {
    var value = cycleRunning?.[input.property];

    if ("numberOfCyclePerHour" === input.property && (value === undefined || "" === value)) {
      value = input.max / 2;
      dispatch(
        setDynamicsCycleRunning({
          ...cycleRunning,
          [input.property]: Number(value)
        })
      );
    }
    return value;
  };

  const inputs = [
    {
      property: "numberOfCyclePerHour",
      label: t("cartesian.ui.dynamics.number_of_cycle/hour"),
      min: 1,
      max: maxNumberCyclesPerHour(moves),
      invalidText: tr("cartesian.ui.dynamics.cycle.val-1-max", {
        max: maxNumberCyclesPerHour(moves)
      })
    },
    {
      property: "workingHoursPerDay",
      label: t("cartesian.ui.dynamics.working_hours/day"),
      min: 1,
      max: 24,
      invalidText: t("cartesian.ui.dynamics.cycle.val-1-24")
    },
    {
      property: "workingDaysPerYear",
      label: t("cartesian.ui.dynamics.working_days/year"),
      min: 1,
      max: 365,
      invalidText: t("cartesian.ui.dynamics.cycle.val-1-365")
    }
  ];

  return (
    <div className="cycle-running" id="cycle-running">
      <SectionHeader data-testid="section-header-cycle-running" title={t("cartesian.ui.dynamics.cycle_running")} />
      <div className="flex space-x-5 p-2 mt-1 reference-value">
        {tr("cartesian.ui.dynamics.total_cycle_time_val_ms", { val: preCalcTotalCycleTime(moves) })}
      </div>
      <div className="flex space-x-5 p-2 mt-1">
        {inputs.map((input, index) => (
          <Input
            data-testid={`input-${input.property}`}
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            type="number"
            pattern="[0-9]*"
            onChange={handleInputChange(input.property)}
            name={input.property}
            value={adaptValue(input)}
            label={input.label}
            classes="max-w-[225px]"
            min={input.min}
            max={input.max}
            minMaxTopLabelShow
            mandatory
            noDecimals
            isInvalid={
              !cycleRunning?.[input.property] ||
              cycleRunning?.[input.property] < input.min ||
              cycleRunning?.[input.property] > input.max
            }
            invalidText={input.invalidText}
          />
        ))}
      </div>
    </div>
  );
};

export default CycleRunning;
